import { alpha, createTheme } from '@mui/material/styles';
import { buildUiTokens } from './Components/ui/tokens';

export const THEME_MODE_STORAGE_KEY = 'outreachos-theme-mode';

const brand = {
  roadHomeGreen: '#006633',
  roadHomeGold: '#F2AE00',
  roadHomeCream: '#FEF3B5',
  rushGreen: '#1D6F42',
  rushLime: '#7AA529',
  rushBlue: '#436092',
};

const darkSurfaces = {
  page: '#111111',
  shell: '#181818',
  card: '#202020',
  cardAlt: '#262626',
  field: '#2b2b2b',
  elevated: '#313131',
};

const lightSurfaces = {
  page: '#f1ede3',
  shell: '#f7f1e6',
  card: '#fcf9f2',
  cardAlt: '#f3ecde',
  field: '#f4edde',
  elevated: '#ece4d4',
};

// Mode-aware semantic colors.
//
// Road Home green stays the identity/action family and gold stays emphasis, but
// each mode picks its own steps so interactive and status foregrounds are legible
// against that mode's normal card surface (light #fcf9f2, dark #202020) rather
// than only against its page background.
const semanticPalettes = {
  light: {
    primary: {
      main: brand.rushGreen,
      light: brand.rushLime,
      dark: brand.roadHomeGreen,
      contrastText: '#ffffff',
    },
    secondary: {
      main: brand.roadHomeGold,
      light: brand.roadHomeCream,
      dark: '#c58900',
      contrastText: '#1b1f16',
    },
    success: {
      main: brand.rushLime,
      light: '#9BC44E',
      dark: '#4E7A16',
      contrastText: '#08110b',
    },
    warning: {
      main: brand.roadHomeGold,
      light: brand.roadHomeCream,
      dark: '#7A5300',
      contrastText: '#231500',
    },
    error: {
      main: '#d32f2f',
      light: '#E25A55',
      dark: '#9E241C',
      contrastText: '#ffffff',
    },
    info: {
      main: brand.rushBlue,
      light: '#6E88B8',
      dark: '#2E4468',
      contrastText: '#ffffff',
    },
  },
  dark: {
    // Rush green on a #202020 card is only ~2.6:1, so dark mode steps primary up
    // to a lighter green and flips its contrast text to ink.
    primary: {
      main: '#5FB07C',
      light: '#8FC9A2',
      dark: '#4A9A66',
      contrastText: '#08130C',
    },
    secondary: {
      main: brand.roadHomeGold,
      light: brand.roadHomeCream,
      dark: '#c58900',
      contrastText: '#1b1f16',
    },
    success: {
      main: brand.rushLime,
      light: '#9BC44E',
      dark: '#6B9425',
      contrastText: '#08110b',
    },
    warning: {
      main: brand.roadHomeGold,
      light: brand.roadHomeCream,
      dark: '#C58900',
      contrastText: '#231500',
    },
    error: {
      main: '#E9827A',
      light: '#F2A9A3',
      dark: '#C9564D',
      contrastText: '#1F0705',
    },
    info: {
      main: '#8FA9D6',
      light: '#B3C5E5',
      dark: '#7392C7',
      contrastText: '#0B1220',
    },
  },
};

function getModeTokens(mode) {
  const isDark = mode === 'dark';
  const surfaces = isDark ? darkSurfaces : lightSurfaces;
  const palette = {
    mode,
    ...semanticPalettes[isDark ? 'dark' : 'light'],
    background: {
      default: surfaces.page,
      paper: surfaces.card,
    },
    text: {
      primary: isDark ? '#F4F0E8' : '#163126',
      secondary: isDark ? '#B5AEA2' : '#556257',
    },
    divider: isDark ? alpha('#F3EBDD', 0.12) : alpha('#163126', 0.1),
  };

  return {
    palette,
    shape: {
      borderRadius: 14,
    },
    custom: {
      shellBackground: surfaces.shell,
      surfaceAlt: surfaces.cardAlt,
      fieldBackground: surfaces.field,
      elevatedSurface: surfaces.elevated,
      cardShadow: isDark
        ? '0 20px 46px rgba(0, 0, 0, 0.28)'
        : '0 18px 40px rgba(46, 38, 23, 0.1)',
      softShadow: isDark
        ? '0 10px 28px rgba(0, 0, 0, 0.22)'
        : '0 10px 24px rgba(41, 34, 21, 0.08)',
      appBarBackground: isDark ? '#1b1b1b' : '#faf5eb',
      drawerBackground: isDark ? '#181818' : '#f6f0e5',
      drawerHover: isDark ? alpha('#fef3b5', 0.05) : alpha(brand.roadHomeGold, 0.08),
      drawerActive: isDark ? alpha('#7AA529', 0.12) : alpha(brand.rushGreen, 0.1),
      drawerActiveBorder: isDark ? alpha('#7AA529', 0.36) : alpha(brand.rushGreen, 0.16),
      drawerActiveText: isDark ? '#FEF3B5' : '#163126',
      drawerSectionLabel: isDark ? '#91897d' : '#6b7367',
      topBarButton: isDark ? '#242424' : '#fffaf0',
      headerGradient: isDark
        ? 'linear-gradient(180deg, rgba(24, 24, 24, 0.98) 0%, rgba(30, 30, 30, 0.96) 100%)'
        : 'linear-gradient(180deg, rgba(250, 245, 235, 0.98) 0%, rgba(245, 238, 226, 0.96) 100%)',
      pageGradient: isDark
        ? 'radial-gradient(circle at top, rgba(242, 174, 0, 0.06), transparent 24%), radial-gradient(circle at 16% 0%, rgba(122, 165, 41, 0.06), transparent 28%), linear-gradient(180deg, #111111 0%, #181818 52%, #202020 100%)'
        : 'radial-gradient(circle at top, rgba(242, 174, 0, 0.14), transparent 28%), radial-gradient(circle at 16% 0%, rgba(29, 111, 66, 0.08), transparent 34%), linear-gradient(180deg, #f8f4ea 0%, #f1ede3 100%)',
      previewFrame: isDark ? '#24211d' : '#e8e0d0',
      previewInset: isDark ? '#fcf9f2' : 'rgba(255,255,255,0.92)',
      previewBorder: isDark ? alpha('#f3ebdd', 0.12) : alpha('#163126', 0.08),
      mutedText: isDark ? '#9f978a' : '#5f6e63',
      accentWash: isDark ? alpha(brand.rushLime, 0.12) : alpha(brand.rushGreen, 0.08),
      successWash: isDark ? alpha(brand.rushLime, 0.16) : alpha(brand.rushLime, 0.1),
      warningWash: isDark ? alpha(brand.roadHomeGold, 0.14) : alpha(brand.roadHomeCream, 0.8),
      dangerWash: isDark ? alpha('#e36d6d', 0.16) : alpha('#b04226', 0.08),
      // UI-005A1: additive semantic namespace for shared presentation components.
      // Existing `theme.custom.*` keys above stay exactly as they are.
      ui: buildUiTokens(mode, palette, {
        page: surfaces.page,
        shell: surfaces.shell,
        card: surfaces.card,
        cardAlt: surfaces.cardAlt,
        inset: surfaces.cardAlt,
        field: surfaces.field,
        raised: surfaces.elevated,
      }),
      brand,
    },
  };
}

// Outlined Chip colors that carry a status meaning, mapped onto the semantic
// status roles. Every other Chip color keeps MUI's own outlined treatment.
const OUTLINED_STATUS_ROLES = {
  success: 'positive',
  warning: 'caution',
};

// Paper hierarchy ramp. `variant="outlined"` is the flat surface; elevation 0 is
// the resting card (the app's default Paper), and every explicit elevation above
// it actually lifts — elevation 1 and 2 must not read as elevation 0.
function paperShadowForElevation(elevation, shadow) {
  const level = Number.isFinite(Number(elevation)) ? Number(elevation) : 0;
  if (level >= 12) return shadow.overlay;
  if (level >= 6) return shadow.raised;
  if (level >= 1) return shadow.card;
  return shadow.soft;
}

export function createAppTheme(mode = 'light') {
  const baseTheme = createTheme({
    ...getModeTokens(mode),
    typography: {
      fontFamily: ['Roboto', 'Helvetica Neue', 'Arial', 'sans-serif'].join(','),
      h4: { fontWeight: 800, letterSpacing: '-0.03em' },
      h5: { fontWeight: 750, letterSpacing: '-0.02em' },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 700 },
      button: { fontWeight: 700, letterSpacing: '0.01em', textTransform: 'none' },
    },
  });

  const ui = baseTheme.custom.ui;

  return createTheme(baseTheme, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: baseTheme.custom.pageGradient,
            color: baseTheme.palette.text.primary,
          },
          '::selection': {
            backgroundColor: alpha(baseTheme.palette.primary.main, 0.28),
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: ui.radius.card,
            backgroundColor: baseTheme.palette.background.paper,
            backgroundImage: 'none',
            transition: 'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
          },
          outlined: {
            border: `1px solid ${ui.border.default}`,
            boxShadow: ui.shadow.none,
          },
          elevation: ({ ownerState }) => ({
            border: `1px solid ${ui.border.subtle}`,
            boxShadow: paperShadowForElevation(ownerState?.elevation, ui.shadow),
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 18,
            border: `1px solid ${baseTheme.palette.divider}`,
            backgroundColor: baseTheme.palette.background.paper,
            boxShadow: baseTheme.custom.softShadow,
            backgroundImage: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: 'none',
            backgroundColor: baseTheme.custom.appBarBackground,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: baseTheme.custom.drawerBackground,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: baseTheme.palette.background.paper,
            border: `1px solid ${baseTheme.palette.divider}`,
            boxShadow: baseTheme.custom.softShadow,
            '&:before': {
              display: 'none',
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 76,
            transition: 'background-color 180ms ease',
            '&:hover': {
              backgroundColor: alpha(baseTheme.palette.primary.main, baseTheme.palette.mode === 'dark' ? 0.08 : 0.04),
            },
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            // ButtonBase resets the native ring with `outline: 0`, which leaves
            // keyboard users with no focus indication. Restore it for keyboard
            // focus only (`.Mui-focusVisible`), leaving pointer focus untouched.
            //
            // An outline rather than a box-shadow: component-level overrides such
            // as MuiButton `contained` declare their own boxShadow and are applied
            // after this one, so a focus shadow declared here would silently lose.
            '&.Mui-focusVisible': {
              outline: ui.focus.outline,
              outlineOffset: ui.focus.offset,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            paddingInline: 16,
            transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            boxShadow: baseTheme.custom.softShadow,
          },
          containedPrimary: {
            backgroundColor: baseTheme.palette.primary.main,
            backgroundImage: 'none',
            color: baseTheme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: baseTheme.palette.primary.dark,
            },
          },
          outlined: {
            borderColor: alpha(baseTheme.palette.primary.main, baseTheme.palette.mode === 'dark' ? 0.34 : 0.24),
            backgroundColor: baseTheme.palette.mode === 'dark' ? alpha('#ffffff', 0.02) : alpha('#ffffff', 0.4),
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'background-color 160ms ease, color 160ms ease, transform 160ms ease',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            borderRadius: ui.radius.pill,
          },
          // Scoped to the default colour only. Applying this to every filled Chip
          // overwrote MUI's success/warning/error/info variants with one green.
          filledDefault: {
            boxShadow: 'none',
            backgroundColor: baseTheme.palette.mode === 'dark'
              ? alpha(baseTheme.palette.primary.main, 0.18)
              : alpha(baseTheme.palette.primary.main, 0.1),
            color: baseTheme.palette.mode === 'dark' ? '#f4fbf7' : baseTheme.palette.primary.dark,
          },
          // The mode-aware wash below is the generic outlined surface and stays
          // exactly as it was. What is corrected here is the foreground: MUI's own
          // `outlinedSuccess`/`outlinedWarning` variants paint the label with
          // `palette.<color>.main`, and Road Home lime (#7AA529) and gold (#F2AE00)
          // are fill colors, not readable foregrounds -- as small chip text they
          // land under 4.5:1. Only those two colors are remapped, onto the semantic
          // status foreground/border roles.
          //
          // The wash is deliberately NOT swapped for `status.<role>.surface`: that
          // translucent positive tint composites to slightly below 4.5:1 behind the
          // same text, so the neutral wash stays and the semantic foreground and
          // border are applied over it.
          outlined: ({ ownerState }) => {
            const backgroundColor = baseTheme.palette.mode === 'dark'
              ? alpha('#ffffff', 0.03)
              : alpha('#ffffff', 0.64);
            const role = OUTLINED_STATUS_ROLES[ownerState?.color];

            if (!role) return { backgroundColor };

            return {
              backgroundColor,
              color: ui.status[role].foreground,
              borderColor: ui.status[role].border,
              // MUI only inherits the icon color when the icon carries the same
              // color as the chip; otherwise it keeps its own palette main, which
              // is the unreadable value this override exists to replace.
              '& .MuiChip-icon': { color: 'inherit' },
            };
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.custom.fieldBackground,
            borderRadius: 12,
            transition: 'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: baseTheme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(baseTheme.palette.primary.main, baseTheme.palette.mode === 'dark' ? 0.44 : 0.34),
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 4px ${alpha(baseTheme.palette.primary.main, baseTheme.palette.mode === 'dark' ? 0.14 : 0.1)}`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: baseTheme.palette.primary.main,
            },
          },
          input: {
            color: baseTheme.palette.text.primary,
            '&::placeholder': {
              color: alpha(baseTheme.palette.text.secondary, 0.82),
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            color: baseTheme.palette.text.secondary,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 2,
            color: baseTheme.custom.mutedText,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            borderRadius: 12,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            transition: 'background-color 160ms ease, color 160ms ease',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 18,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${baseTheme.palette.divider}`,
          },
          head: {
            fontWeight: 750,
            color: baseTheme.palette.text.secondary,
            backgroundColor: alpha(baseTheme.custom.surfaceAlt, baseTheme.palette.mode === 'dark' ? 0.65 : 0.72),
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 160ms ease',
            '&:hover': {
              backgroundColor: `${alpha(baseTheme.palette.primary.main, baseTheme.palette.mode === 'dark' ? 0.1 : 0.04)} !important`,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            backgroundColor: baseTheme.palette.background.paper,
            boxShadow: baseTheme.custom.cardShadow,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: alpha('#020617', 0.56),
            backdropFilter: 'blur(3px)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: alpha(baseTheme.custom.surfaceAlt, 0.96),
            color: baseTheme.palette.text.primary,
            border: `1px solid ${baseTheme.palette.divider}`,
            boxShadow: baseTheme.custom.softShadow,
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            fontWeight: 700,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${baseTheme.palette.divider}`,
          },
        },
      },
    },
  });
}

export function getStoredThemeMode(storage = typeof window !== 'undefined' ? window.localStorage : null) {
  const storedMode = storage?.getItem?.(THEME_MODE_STORAGE_KEY);
  return storedMode === 'light' || storedMode === 'dark' ? storedMode : null;
}

export function getSystemThemeMode(mediaQueryList = typeof window !== 'undefined'
  ? window.matchMedia?.('(prefers-color-scheme: dark)')
  : null) {
  return mediaQueryList?.matches ? 'dark' : 'light';
}

export function resolveInitialThemeMode() {
  return getStoredThemeMode() || getSystemThemeMode();
}

export default createAppTheme('light');
